import { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";

import {
  AZURE,
  INTERACTION,
  PRIMARY,
  formatInteger,
  formatNumber,
  MUTED,
  sectionMetaStyle,
  sectionTitleStyle,
  SOFT_BORDER,
  TEXT,
  tintedBandStyle,
  TURQUOISE
} from "../shared/styles";

const PROJECTION_END_YEAR = 2050;
const SELECTABLE_MEASURE_IDS = ["m3", "alternativePaints"];
const ACTUAL_ALL_SCOPES = TURQUOISE;
const MEASURES_COLOR = INTERACTION;
const TREND_GRAY = "#858585";
const TARGET_GREEN = "#16a34a";
const COST_ORANGE = "#f5a623";
const SCOPE_COLORS = { "Scope 1": "#5944C6", "Scope 2": AZURE, "Scope 3": "#0EABA9" };
const PRESETS = {
  balanced: { label: "Ausgewogen", weights: { cost: 30, co2: 40, roi: 20, funding: 10 } },
  cost: { label: "Fokus Kosten", weights: { cost: 55, co2: 15, roi: 25, funding: 5 } },
  co2: { label: "Fokus CO₂", weights: { cost: 15, co2: 60, roi: 15, funding: 10 } },
  co2100: { label: "Nur CO₂", weights: { cost: 0, co2: 100, roi: 0, funding: 0 } },
  cost100: { label: "Nur Kosten", weights: { cost: 100, co2: 0, roi: 0, funding: 0 } }
};

const WEIGHT_FIELDS = [
  {
    key: "cost",
    label: "Kosteneinsparung"
  },
  {
    key: "co2",
    label: "CO₂-Einsparung"
  },
  {
    key: "roi",
    label: "Amortisation (ROI)"
  },
  {
    key: "funding",
    label: "Förderfähigkeit"
  }
];

const MEASURE_DETAILS = {
  m1: {
    description: "Fossile Heizung durch effiziente Wärmepumpe ersetzen.",
    funding: "Kant. Energieprogramm",
    questions: [
      { q: "Gebäudehülle bereits optimiert?", options: [["Ja", 0.75], ["Teilweise", 1], ["Nein", 1.2]], defaultIndex: 1 },
      { q: "Vorlauftemperatur geeignet?", options: [["Ja", 1.15], ["Unklar", 1], ["Nein", 0.7]], defaultIndex: 1 }
    ]
  },
  m2: {
    description: "Betriebs- und Ersatzfahrzeuge nach Laufleistung elektrifizieren.",
    funding: "-",
    questions: [
      { q: "Fahrzeuge täglich im Einsatz?", options: [["Wenig", 0.7], ["Mittel", 1], ["Hoch", 1.25]], defaultIndex: 1 },
      { q: "Lademöglichkeit am Standort?", options: [["Ja", 1.15], ["Geplant", 1], ["Nein", 0.75]], defaultIndex: 1 }
    ]
  },
  m3: {
    description: "",
    scopes: ["Scope 1", "Scope 2"],
    funding: "Colorwatt bis Juni 2027",
    stats: ["7.8 – 16.2 t CO₂e/Jahr", "3'000 – 7'000 CHF/Jahr", "108'000 – 224'000 CHF", "21 – 43 Jahre"],
    questions: [
      { q: "Kabine älter als 10 Jahre?", options: [["Nein", 0.7], ["Unklar", 1], ["Ja", 1.3]], defaultIndex: 1 },
      { q: "Spotreparaturen in Hauptkabine?", options: [["Selten", 0.75], ["Manchmal", 1], ["Häufig", 1.2]], defaultIndex: 1 }
    ]
  },
  alternativePaints: {
    description: "",
    einsparung_pro_jahr: 19.7, // tCO2e/Jahr; Planungswert: Mittelwert der Bandbreite 12.8–26.6.
    scopes: ["Scope 1", "Scope 2", "Scope 3"],
    funding: "-",
    stats: ["12.8 – 26.6 t CO₂e/Jahr", "3'000 – 6'000 CHF/Jahr", "500 – 1'500 CHF", "0 Jahre"],
    questions: []
  },
  m4: {
    description: "Reparaturquote erhöhen: weniger Ersatzteile, mehr eigene Wertschöpfung.",
    funding: "-",
    questions: [
      { q: "Reparatur standardmässig angeboten?", options: [["Ja, immer", 0.6], ["Teilweise", 0.85], ["Nein", 1.15]], defaultIndex: 1 },
      { q: "Aktuelle Reparaturquote?", options: [["Tief (<60%)", 1.25], ["Mittel (60-75%)", 1], ["Hoch (>75%)", 0.65]], defaultIndex: 1 }
    ]
  },
  m5: {
    description: "Beleuchtung auf LED umstellen und Betriebszeiten optimieren.",
    funding: "ColorWatt",
    questions: [
      { q: "LED bereits teilweise vorhanden?", options: [["Ja", 0.55], ["Teilweise", 0.85], ["Nein", 1.15]], defaultIndex: 1 },
      { q: "Präsenzsteuerung vorhanden?", options: [["Ja", 0.7], ["Nein", 1.1]], defaultIndex: 1 }
    ]
  }
};

// Kostenparameter relativ zu m3 kalibriert (Kosteneinsparung ~92 %, Investition ~0.6 % von m3).
const ALTERNATIVE_PAINTS_COST_MODEL = {
  relative_kosteneinsparung: 50, // Prozent
  anteil_gesamtkosten: 57.6, // Prozent
  relativ_investition: 1.53, // Prozent
  investitionsbedarf: 1000, // CHF
  amortisationszeit: 0 // Jahre
};

function detailFor(measure) {
  return MEASURE_DETAILS[measure.id] || { description: measure.beschreibung || "Relevanter Beitrag zur Emissionsreduktion.", funding: "-", questions: [] };
}

function defaultAnswers(measure) {
  return detailFor(measure).questions.map((question) => question.defaultIndex || 0);
}

function factorFor(measure, answers) {
  const questions = detailFor(measure).questions;
  return questions.reduce((factor, question, index) => factor * (question.options[answers?.[index] ?? question.defaultIndex ?? 0]?.[1] ?? 1), 1);
}

function effectiveMeasure(measure, answers, isRefined) {
  if (!isRefined) return measure;
  const factor = factorFor(measure, answers);
  return {
    ...measure,
    einsparung_pro_jahr: (Number(measure.einsparung_pro_jahr) || 0) * factor,
    investitionsbedarf: Math.max(0, Math.round((Number(measure.investitionsbedarf) || 0) * (0.85 + factor * 0.15)))
  };
}

function paybackYears(measure) {
  if (Number(measure.amortisationszeit) > 0) return Number(measure.amortisationszeit);
  const yearlySavings = (Number(measure.investitionsbedarf) || 0) / Math.max(Number(measure.amortisationszeit) || 1, 1);
  return yearlySavings > 0 ? (Number(measure.investitionsbedarf) || 0) / yearlySavings : Infinity;
}

function normalize(value, min, max, invert = false) {
  if (max <= min) return 0;
  const normalized = (value - min) / (max - min);
  return invert ? 1 - normalized : normalized;
}

function buildScoredMeasures(measures, weights, answersById, refinedIds) {
  const enriched = (measures || []).map((measure) => {
    const detail = detailFor(measure);
    const isRefined = refinedIds.has(measure.id);
    const effective = effectiveMeasure(measure, answersById[measure.id] || defaultAnswers(measure), isRefined);
    return {
      ...effective,
      original: measure,
      description: detail.description,
      funding: detail.funding,
      isRefined,
      payback: paybackYears(effective)
    };
  });

  const co2Values = enriched.map((m) => Number(m.einsparung_pro_jahr) || 0);
  const costValues = enriched.map((m) => Number(m.relative_kosteneinsparung) || 0);
  const roiValues = enriched.map((m) => Number.isFinite(m.payback) ? m.payback : 999);
  const ranges = {
    co2: [Math.min(...co2Values), Math.max(...co2Values)],
    cost: [Math.min(...costValues), Math.max(...costValues)],
    roi: [Math.min(...roiValues), Math.max(...roiValues)]
  };
  const totalWeight = Math.max(Object.values(weights).reduce((sum, value) => sum + value, 0), 1);

  return enriched
    .map((measure) => {
      const fundingScore = measure.funding && measure.funding !== "-" ? 1 : 0;
      const score = (
        normalize(Number(measure.relative_kosteneinsparung) || 0, ...ranges.cost) * weights.cost +
        normalize(Number(measure.einsparung_pro_jahr) || 0, ...ranges.co2) * weights.co2 +
        normalize(Number.isFinite(measure.payback) ? measure.payback : 999, ...ranges.roi, true) * weights.roi +
        fundingScore * weights.funding
      ) / totalWeight * 100;
      return { ...measure, priorityScore: Math.round(score) };
    })
    .sort((a, b) => b.priorityScore - a.priorityScore);
}

function buildSeries(yearlyTotals, selectedMeasures) {
  const sorted = [...(yearlyTotals || [])].sort((a, b) => a.year - b.year);
  if (sorted.length === 0) return { data: [], firstYearTotal: 0 };

  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const yearsDiff = last.year - first.year;
  const annualChange = yearsDiff > 0 ? (last.actual - first.actual) / yearsDiff : 0;
  const yearMap = new Map(sorted.map((d) => [d.year, { year: d.year, actual: d.actual }]));

  for (let year = last.year; year <= PROJECTION_END_YEAR; year += 1) {
    const existing = yearMap.get(year) || { year };
    yearMap.set(year, { ...existing, trajectory: Math.max(last.actual + (year - last.year) * annualChange, 0) });
  }

  const rows = Array.from(yearMap.values()).sort((a, b) => a.year - b.year);
  rows.forEach((row) => {
    const base = Number.isFinite(row.actual) ? row.actual : Number.isFinite(row.trajectory) ? row.trajectory : 0;
    let reduction = 0;
    let investCarrySum = 0;
    let steadyReductionPP = 0;

    (selectedMeasures || []).forEach((m) => {
      const start = Number(m.startYear);
      if (Number.isNaN(start) || row.year < start) return;
      reduction += Number(m.einsparung_pro_jahr) || 0;
      const investBoost = Number(m.relativ_investition) || 0;
      const share = (Number(m.anteil_gesamtkosten) || 0) / 100;
      const savingPct = (Number(m.relative_kosteneinsparung) || 0) / 100;
      const yearsSince = row.year - start;
      if (yearsSince === 0) investCarrySum += investBoost;
      if (yearsSince >= 1) steadyReductionPP += share * savingPct * 100;
    });

    row.withMeasures = selectedMeasures?.length ? Math.max(base - reduction, 0) : undefined;
    row.costIndex = selectedMeasures?.length ? Math.max(100 + investCarrySum - steadyReductionPP, 0) : undefined;
    row.netZeroPath = first.actual > 0 ? Math.max(first.actual * (1 - (row.year - first.year) / (PROJECTION_END_YEAR - first.year)), 0) : undefined;
  });

  return { data: rows, firstYearTotal: first.actual };
}

function ToggleCell({ checked, onChange, label, disabled = false }) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{ flex: "none", width: 42, height: 24, borderRadius: 999, background: checked ? PRIMARY : "#d4dacd", border: "none", padding: 0, cursor: disabled ? "not-allowed" : "pointer", position: "relative", opacity: disabled ? 0.55 : 1 }}
    >
      <span style={{ position: "absolute", top: 2, left: checked ? 20 : 2, width: 20, height: 20, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,.2)", transition: "left .18s" }} />
    </button>
  );
}

export default function PathwaySection({
  yearlyTotals,
  measures,
  selectedMeasures,
  onToggleMeasure,
  onChangeStartYear,
  onUnlockClick,
  onExportMeasuresChange,
  isMobile
}) {
  const [preset, setPreset] = useState("balanced");

  const [weights, setWeights] =
    useState(PRESETS.balanced.weights);

  const [showCustomWeights, setShowCustomWeights] =
    useState(false);

  const [openRefinements, setOpenRefinements] =
    useState({});
  const [refinedIds, setRefinedIds] = useState(() => new Set());
  const [answersById, setAnswersById] = useState({});

  const selectedById = new Map((selectedMeasures || []).map((measure) => [measure.id, measure]));
  const scoredMeasures = useMemo(() => buildScoredMeasures(measures, weights, answersById, refinedIds), [measures, weights, answersById, refinedIds]);
  const selectableMeasures = useMemo(() => [
    ...scoredMeasures.filter((measure) => measure.id === "m3"),
    { id: "alternativePaints", name: "Lufttrocknende Lacke", ...ALTERNATIVE_PAINTS_COST_MODEL, ...MEASURE_DETAILS.alternativePaints }
  ], [scoredMeasures]);
  const displayedMeasures = [
    ...selectableMeasures,
    ...scoredMeasures.filter((measure) => !SELECTABLE_MEASURE_IDS.includes(measure.id))
  ];
  const areAllMeasuresSelected = selectableMeasures.every((measure) => selectedById.has(measure.id));
  const effectiveSelectedMeasures = useMemo(
    () =>
      (selectedMeasures || []).filter((selected) => SELECTABLE_MEASURE_IDS.includes(selected.id)).map((selected) => {
        const scored = selectableMeasures.find((measure) => measure.id === selected.id);
        return { ...(scored || selected), startYear: selected.startYear ?? new Date().getFullYear() };
      }),
    [selectedMeasures, selectableMeasures]
  );

  useEffect(() => {
    (selectedMeasures || []).forEach((measure) => {
      if (!SELECTABLE_MEASURE_IDS.includes(measure.id)) onToggleMeasure?.(measure, false);
    });
  }, [selectedMeasures, onToggleMeasure]);

  useEffect(() => {
    onExportMeasuresChange?.(effectiveSelectedMeasures);
  }, [effectiveSelectedMeasures, onExportMeasuresChange]);

  const { data, firstYearTotal } = buildSeries(yearlyTotals, effectiveSelectedMeasures);
  const lastActualYear = yearlyTotals?.length ? Math.max(...yearlyTotals.map((row) => row.year)) : new Date().getFullYear();
  const visibleChartEndYear = Math.min(lastActualYear + 10, PROJECTION_END_YEAR);
  const chartData = data.filter((row) => row.year <= visibleChartEndYear);
  const targetMax = firstYearTotal * 0.1;
  const hasMeasures = effectiveSelectedMeasures.length > 0;
  const projection2050 = data.find((d) => d.year === PROJECTION_END_YEAR)?.withMeasures
    ?? data.find((d) => d.year === PROJECTION_END_YEAR)?.trajectory;
  const reductionPct = projection2050 != null && firstYearTotal > 0 ? ((firstYearTotal - projection2050) / firstYearTotal) * 100 : null;
  const totalInvestment = effectiveSelectedMeasures.reduce((sum, m) => sum + (m.investitionsbedarf || 0), 0);
  const annualSavings = effectiveSelectedMeasures.reduce((sum, m) => sum + (Number(m.einsparung_pro_jahr) || 0), 0);
  const fundingCount = effectiveSelectedMeasures.filter((m) => m.funding && m.funding !== "-").length;
  const paybackValues = effectiveSelectedMeasures
    .map((m) => Number(m.amortisationszeit))
    .filter((value) => Number.isFinite(value) && value > 0);
  const averagePayback = paybackValues.length
    ? paybackValues.reduce((sum, value) => sum + value, 0) / paybackValues.length
    : null;
  const thisYear = new Date().getFullYear();
  const refinedCount = refinedIds.size;

  function applyPreset(nextPreset) {
    setPreset(nextPreset);

    setWeights({
      ...PRESETS[nextPreset].weights
    });
  }

  function handleWeightChange(key, value) {
    setPreset(null);

    setWeights((previous) => {
      const changedWeight = Math.max(
        0,
        Math.min(100, Math.round(Number(value) || 0))
      );
      const otherKeys = WEIGHT_FIELDS
        .map((field) => field.key)
        .filter((fieldKey) => fieldKey !== key);
      const remainingWeight = 100 - changedWeight;
      const previousOtherTotal = otherKeys.reduce(
        (sum, fieldKey) => sum + Number(previous[fieldKey] || 0),
        0
      );
      const exactOtherWeights = otherKeys.map((fieldKey) => ({
        key: fieldKey,
        value: previousOtherTotal > 0
          ? (Number(previous[fieldKey] || 0) / previousOtherTotal) * remainingWeight
          : remainingWeight / otherKeys.length
      }));
      const roundedOtherWeights = exactOtherWeights.map((weight) => ({
        ...weight,
        rounded: Math.floor(weight.value)
      }));
      let roundingRemainder = remainingWeight - roundedOtherWeights.reduce(
        (sum, weight) => sum + weight.rounded,
        0
      );

      roundedOtherWeights
        .sort((a, b) => (b.value - b.rounded) - (a.value - a.rounded))
        .forEach((weight) => {
          if (roundingRemainder <= 0) return;
          weight.rounded += 1;
          roundingRemainder -= 1;
        });

      const nextWeights = { [key]: changedWeight };
      roundedOtherWeights.forEach((weight) => {
        nextWeights[weight.key] = weight.rounded;
      });

      return nextWeights;
    });
  }

  function normalizedWeight(key) {
    return Number(weights[key] || 0);
  }

  function handleAnswer(measureId, questionIndex, answerIndex) {
    setAnswersById((prev) => {
      const measure = (measures || []).find((m) => m.id === measureId);
      const current = prev[measureId] || defaultAnswers(measure);
      return { ...prev, [measureId]: current.map((value, index) => (index === questionIndex ? answerIndex : value)) };
    });
  }

  function handleApplyRefinement(measureId) {
    setRefinedIds((prev) => new Set([...prev, measureId]));
  }

  function handleRevertRefinement(measureId) {
    setRefinedIds((prev) => {
      const next = new Set(prev);
      next.delete(measureId);
      return next;
    });
  }

  function handleToggleAll(checked) {
    selectableMeasures.forEach((measure) => {
      if (!!selectedById.get(measure.id) !== checked) onToggleMeasure?.(measure, checked);
    });
  }

  return (
    <>
      <section style={{ ...tintedBandStyle, overflowX: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", alignItems: "baseline", marginBottom: "1rem" }}>
          <div>
            <h2 className="h2" style={sectionTitleStyle}>CO₂-Verlauf &amp; Projektion</h2>
            <p style={sectionMetaStyle}>Ist-Werte sowie Investitions- &amp; Energiekosten der nächsten 10 Jahre im Verlauf; KPI-Projektion bis {PROJECTION_END_YEAR}.</p>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(5, minmax(0, 1fr))", gap: "0.75rem", marginBottom: "1rem" }}>
          <KpiCard label={`CO₂ heute`} value={firstYearTotal > 0 ? formatNumber(firstYearTotal, 1) : "-"} unit="t/Jahr" />
          <KpiCard
            label={`Projektion ${PROJECTION_END_YEAR}`}
            value={projection2050 != null ? formatNumber(projection2050, 1) : "-"}
            unit="t/Jahr"
            helper={reductionPct != null ? `${formatNumber(reductionPct, 0)}% ggü. heute` : null}
            positive={reductionPct > 0}
          />
          <KpiCard label="Jährliche Einsparung" value={annualSavings ? formatNumber(annualSavings, 1) : "-"} unit="t/Jahr" helper="bei Vollwirkung" positive={annualSavings > 0} />
          <KpiCard label="Investition gesamt" value={totalInvestment ? formatInteger(totalInvestment) : "-"} unit="CHF" helper={averagePayback != null ? `Ø Amortisation ${formatNumber(averagePayback, 1)} Jahre` : null} />
          <KpiCard label="Förderfähig" value={fundingCount || "-"} unit="Massnahmen" helper="mit kant./eidg. Programm" />
        </div>

        {firstYearTotal > 0 && (
          <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", padding: "0.4rem 0.85rem", marginBottom: "0.75rem", borderRadius: 999, background: "#ecfdf3", border: "1px solid #86efac", fontSize: "0.85rem", fontWeight: 600, color: "#166534" }}>
            <span style={{ display: "inline-block", width: 18, height: 10, borderRadius: 999, background: "rgba(22, 163, 74, 0.18)", border: "1px solid rgba(22, 163, 74, 0.45)" }} />
            Zielbereich: &lt;= {targetMax.toFixed(0)} t
          </div>
        )}

        {chartData.length > 1 ? (
          <ResponsiveContainer width="100%" height={isMobile ? 280 : 320}>
            <LineChart data={chartData} margin={{ top: 40, right: hasMeasures ? 100 : 70, left: isMobile ? 0 : 70, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" tick={{ fontSize: 12, fill: MUTED }} />
              <YAxis yAxisId="left" label={isMobile ? undefined : { value: "t CO₂ / Jahr", angle: -90, position: "outsideLeft", dx: -40, style: { fontWeight: 600, fill: "#4b5563" } }} tickFormatter={(v) => v.toFixed(0)} tick={{ fontSize: 12, fill: MUTED }} />
              {hasMeasures && <YAxis yAxisId="right" orientation="right" label={isMobile ? undefined : { value: "Kosten (%)", angle: 90, position: "outsideRight", dx: 40, style: { fontWeight: 600, fill: "#4b5563" } }} tickFormatter={(v) => `${v.toFixed(0)} %`} tick={{ fontSize: 12, fill: MUTED }} />}
              <Tooltip formatter={(v, name) => (name?.includes("Kosten") ? `${formatNumber(v, 2)} %` : `${formatNumber(v, 2)} t CO₂`)} contentStyle={{ backgroundColor: "rgba(255,255,255,0.95)", border: "1px solid #ccc", borderRadius: 8, padding: "10px 12px" }} labelStyle={{ fontWeight: 600, marginBottom: "0.4rem" }} />
              <Legend wrapperStyle={{ fontSize: "0.82rem" }} />
              {firstYearTotal > 0 && <ReferenceArea yAxisId="left" y1={0} y2={targetMax} fill={TARGET_GREEN} fillOpacity={0.12} stroke={TARGET_GREEN} strokeOpacity={0.4} strokeDasharray="4 2" />}
              <Line yAxisId="left" type="monotone" dataKey="actual" name="Ist-Emissionen" stroke={ACTUAL_ALL_SCOPES} dot={{ r: 4 }} activeDot={{ r: 6 }} connectNulls={false} />
              <Line yAxisId="left" type="monotone" dataKey="trajectory" name="Trend ohne Massnahmen" stroke={TREND_GRAY} strokeDasharray="5 5" dot={false} connectNulls />
              <Line yAxisId="left" type="monotone" dataKey="netZeroPath" name="Absenkpfad Netto-Null" stroke={TARGET_GREEN} strokeDasharray="2 4" dot={false} connectNulls />
              {hasMeasures && <Line yAxisId="left" type="monotone" dataKey="withMeasures" name="Mit gewählten Massnahmen" stroke={MEASURES_COLOR} strokeDasharray="5 5" dot={{ r: 4 }} connectNulls />}
              {hasMeasures && <Line yAxisId="right" type="monotone" dataKey="costIndex" name="Kostenverlauf" stroke={COST_ORANGE} strokeWidth={2} strokeDasharray="4 2" dot={{ r: 3 }} connectNulls />}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p style={{ color: MUTED, fontSize: "0.9rem" }}>Zu wenig historische Daten für eine Projektion.</p>
        )}
        {hasMeasures && chartData.length > 1 && (
          <p style={{ color: MUTED, fontSize: "0.76rem", marginTop: "0.5rem" }}>
            Hinweis: nur Energiekosten (Strom, Heizung, Treibstoff).
          </p>
        )}
      </section>

      <section style={tintedBandStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", alignItems: "baseline" }}>
          <div>
            <h2 className="h2" style={sectionTitleStyle}>Massnahmen</h2>
            <p style={sectionMetaStyle}>Nach Priorität sortiert. Stufe 2 verfeinert Einsparung und Investition über Folgefragen.</p>
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "0.55rem", padding: "0.35rem 0.55rem", border: `1px solid ${SOFT_BORDER}`, borderRadius: 999, background: "#fff" }}>
            <span style={{ fontSize: "0.78rem", color: MUTED, fontWeight: 700 }}>Alle Massnahmen</span>
            <ToggleCell checked={areAllMeasuresSelected} label="Alle Massnahmen ein- oder ausschalten" onChange={() => handleToggleAll(!areAllMeasuresSelected)} />
          </div>
        </div>
        <div
          style={{
            marginTop: "1rem",
            padding: "0.8rem",
            background: "#fff",
            border: `1px solid ${SOFT_BORDER}`,
            borderRadius: 8
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              flexWrap: "wrap"
            }}
          >
            <span
              style={{
                fontSize: "0.78rem",
                fontWeight: 700,
                color: "#51606d",
                textTransform: "uppercase"
              }}
            >
              Priorisierung
            </span>

            {Object.entries(PRESETS).map(([key, config]) => (
              <button
                key={key}
                type="button"
                onClick={() => applyPreset(key)}
                style={{
                  ...smallButtonStyle,
                  background: preset === key ? PRIMARY : "#fff",
                  color: preset === key ? "#fff" : "#51606d",
                  borderColor: preset === key ? PRIMARY : SOFT_BORDER
                }}
              >
                {config.label}
              </button>
            ))}

            <button
              type="button"
              onClick={() =>
                setShowCustomWeights((previous) => !previous)
              }
              aria-expanded={showCustomWeights}
              style={{
                ...smallButtonStyle,
                marginLeft: isMobile ? 0 : "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.4rem"
              }}
            >
              Individuell anpassen
              <span>
                {showCustomWeights ? "▲" : "▼"}
              </span>
            </button>
          </div>

          {showCustomWeights && (
            <div
              style={{
                display: "grid",
                gap: "0.7rem",
                marginTop: "0.85rem",
                paddingTop: "0.8rem",
                borderTop: `1px solid ${SOFT_BORDER}`
              }}
            >
              {WEIGHT_FIELDS.map(({ key, label }) => (
                <div
                  key={key}
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr 52px"
                      : "150px minmax(0, 1fr) 52px",
                    gridTemplateAreas: isMobile
                      ? `"label label" "slider value"`
                      : `"label slider value"`,
                    alignItems: "center",
                    gap: "0.5rem 0.75rem"
                  }}
                >
                  <label
                    htmlFor={`priority-${key}`}
                    style={{
                      gridArea: "label",
                      fontSize: "0.78rem",
                      color: "#51606d"
                    }}
                  >
                    {label}
                  </label>

                  <input
                    id={`priority-${key}`}
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={weights[key]}
                    onChange={(event) =>
                      handleWeightChange(
                        key,
                        event.target.value
                      )
                    }
                    style={{
                      gridArea: "slider",
                      width: "100%",
                      accentColor: PRIMARY,
                      cursor: "pointer"
                    }}
                  />

                  <span
                    style={{
                      gridArea: "value",
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      color: PRIMARY,
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums"
                    }}
                  >
                    {normalizedWeight(key)} %
                  </span>
                </div>
              ))}

              <p
                style={{
                  margin: "0.15rem 0 0",
                  fontSize: "0.72rem",
                  color: MUTED,
                  lineHeight: 1.45
                }}
              >
                Gewichte werden automatisch auf 100 % normiert.
                Der Prioritäts-Score jeder Massnahme ergibt sich aus
                Kosteneinsparung, CO₂-Einsparung, Amortisation und
                Förderfähigkeit.
              </p>
            </div>
          )}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr)",
            gap: "0.75rem",
            maxHeight: 620,
            overflowY: "auto",
            overflowX: "hidden",
            marginTop: "0.85rem",
            paddingRight: 4
          }}
        >
          {displayedMeasures.map((measure) => {
            const selected = selectedById.get(measure.id);
            const checked = SELECTABLE_MEASURE_IDS.includes(measure.id) && !!selected;
            const answers = answersById[measure.id] || defaultAnswers(measure);
            const factor = factorFor(measure.original || measure, answers);
            const stats = detailFor(measure).stats;
            const tier = stats ? "teaser" : "locked";
            const isLocked = tier === "locked";
            const isTeaser = tier === "teaser";
            const isOpen = !isLocked && !!openRefinements[measure.id];
            return (
              <div key={measure.id} style={{ border: `1px solid ${checked ? "#cfe0d4" : SOFT_BORDER}`, borderRadius: 12, background: checked ? "#fbfdfb" : "#fff", overflow: "hidden", opacity: checked ? 1 : 0.76 }}>
                <div style={{ display: "flex", gap: "0.8rem", padding: "0.85rem", alignItems: "flex-start", position: "relative" }}>
                  <div style={{ display: "flex", gap: "0.8rem", flex: 1, minWidth: 0, position: "relative" }}>
                    <div style={{ display: "flex", gap: "0.8rem", flex: 1, minWidth: 0, filter: isLocked ? "blur(5px)" : "none", opacity: isLocked ? 0.55 : 1, pointerEvents: isLocked ? "none" : "auto", userSelect: isLocked ? "none" : "auto" }}>
                      <span style={{ width: 10, height: 10, borderRadius: "50%", background: SCOPE_COLORS[measure.scope] || AZURE, marginTop: 6, flex: "none" }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                          <strong style={{ fontSize: "0.95rem" }}>{measure.name}</strong>
                          {(detailFor(measure).scopes || (measure.scope ? [measure.scope] : [])).map((scope) => (
                            <span key={scope} style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 999, color: SCOPE_COLORS[scope] || AZURE, background: "#f1f5f9" }}>{scope}</span>
                          ))}
                          {measure.isRefined && <span style={{ fontSize: 10, fontWeight: 700, color: "#0f6b3a", background: "#e7f2ea", padding: "2px 7px", borderRadius: 999 }}>Stufe 2 verfeinert</span>}
                        </div>
                        {measure.description && <div style={{ fontSize: "0.78rem", color: MUTED, marginTop: "0.2rem" }}>{measure.description}</div>}

                        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "0.5rem 0.9rem", marginTop: "0.75rem" }}>
                          <MeasureStat label="CO₂-Einsparung" value={stats?.[0] ?? `-${formatNumber(measure.einsparung_pro_jahr, 1)} t/Jahr`} positive />
                          <MeasureStat label="Kosteneinsparung" value={stats?.[1] ?? `-${formatInteger((measure.anteil_gesamtkosten || 0) * (measure.relative_kosteneinsparung || 0) * 10)} CHF`} positive />
                          <MeasureStat label="Investitionskosten" value={stats?.[2] ?? `${formatInteger(measure.investitionsbedarf)} CHF`} />
                          <MeasureStat label="Amortisationszeit" value={stats?.[3] ?? (measure.amortisationszeit > 0 ? `${formatNumber(measure.amortisationszeit, 1)} Jahre` : "sofort")} />
                        </div>

                        <div
                          style={{
                            marginTop: "0.65rem",
                            fontSize: "0.78rem",
                            color: measure.funding !== "-" ? INTERACTION : "#97a2af",
                            ...(measure.funding !== "-" && {
                              width: "fit-content",
                              maxWidth: "100%",
                              boxSizing: "border-box",
                              padding: "0.5rem 0.7rem",
                              border: `1px solid ${INTERACTION}`,
                              borderRadius: 8,
                              background: "#e8f4f3",
                              fontWeight: 600,
                              lineHeight: 1.4,
                              overflowWrap: "anywhere"
                            })
                          }}
                        >
                          Fördergelder: {measure.funding !== "-" ? measure.funding : "keine"}
                        </div>

                        {Number.isFinite(measure.priorityScore) && <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", marginTop: "0.65rem" }}>
                          <span style={{ fontSize: "0.76rem", color: MUTED, fontWeight: 600 }}>Priorität</span>
                          <span style={{ flex: 1, height: 5, borderRadius: 3, background: "#eef0ec", overflow: "hidden" }}>
                            <span
                              style={{
                                display: "block",
                                height: "100%",
                                width: `${measure.priorityScore}%`,
                                background: INTERACTION
                              }}
                            />
                          </span>
                          <span style={{ fontSize: "0.76rem", color: MUTED, fontWeight: 700 }}>{measure.priorityScore}</span>
                        </div>}

                        {!isLocked && (
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.75rem", fontSize: "0.78rem", color: MUTED, fontWeight: 600 }}>
                            Startjahr
                            <StartYearStepper
                              name={measure.name}
                              value={selected?.startYear ?? thisYear}
                              min={thisYear}
                              max={Math.max(thisYear, PROJECTION_END_YEAR)}
                              disabled={!checked}
                              onChange={(year) => onChangeStartYear?.(measure.id, year)}
                            />
                          </div>
                        )}

                        <div style={{ position: "relative" }}>
                          <div style={{ filter: isTeaser ? "blur(4px)" : "none", opacity: isTeaser ? 0.6 : 1, pointerEvents: isTeaser ? "none" : "auto", userSelect: isTeaser ? "none" : "auto" }}>
                            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center", marginTop: "0.7rem" }}>
                              <button type="button" onClick={() => setOpenRefinements((prev) => ({ ...prev, [measure.id]: !prev[measure.id] }))} style={refineButtonStyle}>
                                {isOpen ? "Folgefragen schliessen" : measure.isRefined ? "Stufe 2 anpassen" : "Stufe 2 verfeinern"}
                                <span style={{ fontSize: "0.78rem", lineHeight: 1 }}>{isOpen ? "▲" : "▼"}</span>
                              </button>
                            </div>

                            {checked && (
                              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", flexWrap: "wrap", marginTop: "0.75rem", paddingTop: "0.7rem", borderTop: `1px solid ${SOFT_BORDER}` }}>
                                <button type="button" style={actionButtonStyle}>Angebote einholen</button>
                                {measure.funding !== "-" && <button type="button" style={actionButtonStyle}>Fördergelder prüfen</button>}
                              </div>
                            )}
                          </div>
                          {isTeaser && (
                            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <sdx-button theme="secondary" label="Basic freischalten" icon-name="icon-lock" onClick={onUnlockClick}></sdx-button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {isLocked && (
                      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: "0.5rem" }}>
                        <sdx-button theme="secondary" label="Basic freischalten" icon-name="icon-lock" onClick={onUnlockClick}></sdx-button>
                      </div>
                    )}
                  </div>
                  

                  <ToggleCell checked={checked} disabled={isLocked} label={`Massnahme ${measure.name}`} onChange={(nextChecked) => {
                    if (!isLocked) onToggleMeasure?.(measure, nextChecked);
                  }} />
                </div>

                {isOpen && (
                  <div style={{ borderTop: `1px dashed ${SOFT_BORDER}`, padding: "0.85rem", background: "#fbfcfa" }}>
                    <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#51606d", marginBottom: "0.65rem" }}>Stufe 2 - Folgefragen beantworten</div>
                    <div style={{ display: "grid", gap: "0.6rem" }}>
                      {detailFor(measure.original || measure).questions.map((question, qIndex) => (
                        <label key={question.q} style={{ display: "grid", gap: "0.25rem", fontSize: "0.78rem", color: MUTED }}>
                          {question.q}
                          <select value={answers[qIndex]} onChange={(e) => handleAnswer(measure.id, qIndex, Number(e.target.value))} style={{ font: "inherit", padding: "7px 9px", border: `1px solid ${SOFT_BORDER}`, borderRadius: 8, background: "#fff", color: TEXT }}>
                            {question.options.map((option, optionIndex) => <option key={option[0]} value={optionIndex}>{option[0]}</option>)}
                          </select>
                        </label>
                      ))}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", alignItems: "center", marginTop: "0.8rem", padding: "0.65rem", background: "#e7f2ea", borderRadius: 9 }}>
                      <div>
                        <div style={{ fontSize: "0.74rem", color: "#0f6b3a" }}>Verfeinerte Einsparung</div>
                        <strong style={{ color: "#0f6b3a" }}>-{formatNumber((measure.original?.einsparung_pro_jahr || measure.einsparung_pro_jahr) * factor, 1)} t/Jahr</strong>
                      </div>
                      <span style={{ fontSize: "0.76rem", color: "#0f6b3a" }}>Faktor x{factor.toFixed(2)}</span>
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "0.7rem" }}>
                      {measure.isRefined ? (
                        <button type="button" onClick={() => handleRevertRefinement(measure.id)} style={linkButtonStyle}>Zurück zu Stufe 1</button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleApplyRefinement(measure.id)}
                          style={{
                            ...smallButtonStyle,
                            background: PRIMARY,
                            borderColor: PRIMARY,
                            color: "#fff"
                          }}
                        >
                          Stufe 2 übernehmen
                        </button>
                      )}
                      <button type="button" disabled style={{ ...smallButtonStyle, color: "#97a2af", cursor: "not-allowed" }}>Stufe 3: Offerte</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ textAlign: "right", fontSize: "0.76rem", color: MUTED, marginTop: "0.45rem" }}>{refinedCount} von {scoredMeasures.length} verfeinert</div>
      </section>
    </>
  );
}

const smallButtonStyle = {
  font: "inherit",
  fontSize: "0.78rem",
  fontWeight: 600,
  border: `1px solid ${SOFT_BORDER}`,
  background: "#fff",
  color: "#51606d",
  padding: "0.38rem 0.7rem",
  borderRadius: 8,
  cursor: "pointer"
};

const refineButtonStyle = {
  ...smallButtonStyle,
  display: "inline-flex",
  alignItems: "center",
  gap: "0.45rem",
  borderColor: INTERACTION,
  background: "#fff",
  color: INTERACTION,
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.08)"
};

const actionButtonStyle = {
  font: "inherit",
  fontSize: "0.78rem",
  fontWeight: 700,
  border: `1px solid ${INTERACTION}`,
  background: "#fff",
  color: INTERACTION,
  padding: "0.44rem 0.75rem",
  borderRadius: 8,
  cursor: "pointer",
  whiteSpace: "nowrap"
};

function KpiCard({ label, value, unit, helper, positive }) {
  return (
    <div style={{ background: "#fff", border: `1px solid ${SOFT_BORDER}`, borderRadius: 8, padding: "0.85rem", minWidth: 0 }}>
      <div style={{ fontSize: "0.72rem", color: MUTED, fontWeight: 700, minHeight: "1.9rem" }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: "0.35rem", marginTop: "0.2rem", color: positive ? "#0f6b3a" : TEXT }}>
        <strong style={{ fontSize: "1.35rem", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{value}</strong>
        <span style={{ fontSize: "0.76rem", color: MUTED, fontWeight: 600 }}>{unit}</span>
      </div>
      {helper && <div style={{ fontSize: "0.74rem", color: positive ? "#0f6b3a" : MUTED, marginTop: "0.35rem" }}>{helper}</div>}
    </div>
  );
}

const linkButtonStyle = {
  border: "none",
  background: "transparent",
  color: INTERACTION,
  font: "inherit",
  fontSize: "0.78rem",
  textDecoration: "underline",
  cursor: "pointer",
  padding: 0
};

function StartYearStepper({ name, value, min, max, disabled, onChange }) {
  const buttonStyle = {
    width: 34, height: 34, border: "none", background: "transparent",
    font: "inherit", fontSize: "1.1rem", color: TEXT
  };
  return (
    <div style={{ display: "inline-flex", alignItems: "center", border: `1px solid ${SOFT_BORDER}`, borderRadius: 8, overflow: "hidden", background: disabled ? "#f2f4f7" : "#fff" }}>
      <button type="button" aria-label={`Startjahr ${name} verringern`} disabled={disabled || value <= min} onClick={() => onChange(Math.max(min, Number(value) - 1))} style={{ ...buttonStyle, opacity: disabled || value <= min ? 0.35 : 1, cursor: disabled || value <= min ? "default" : "pointer" }}>−</button>
      <output aria-label={`Startjahr ${name}`} style={{ minWidth: 48, textAlign: "center", color: TEXT, fontVariantNumeric: "tabular-nums" }}>{value}</output>
      <button type="button" aria-label={`Startjahr ${name} erhöhen`} disabled={disabled || value >= max} onClick={() => onChange(Math.min(max, Number(value) + 1))} style={{ ...buttonStyle, opacity: disabled || value >= max ? 0.35 : 1, cursor: disabled || value >= max ? "default" : "pointer" }}>+</button>
    </div>
  );
}

function MeasureStat({ label, value, positive }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: "0.72rem", color: MUTED }}>{label}</div>
      <strong style={{ display: "block", color: positive ? "#0f6b3a" : TEXT, fontSize: "0.84rem", fontVariantNumeric: "tabular-nums" }}>{value}</strong>
    </div>
  );
}
